import { useState } from "react";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Sparkles, BookOpen, Image, Snowflake, Users, Inbox, Settings, LogOut, Loader2, Menu, X, Ticket, Mail, Lock, FileText, LayoutList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCMS } from "@/hooks/useCMS";
import { NAV_GROUPS } from "@/pages/admin/schemas";

const ICONS = { LayoutDashboard, CalendarDays, Sparkles, BookOpen, Image, Snowflake, Users, Inbox, Settings, Ticket, Mail, Lock, FileText, LayoutList };

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { data } = useCMS();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2CACD4]" size={32} /></div>;
  }
  if (!user || !user.id) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  const newCount = (data.submissions || []).filter?.((s) => s.status === "new").length;

  return (
    <div className="min-h-screen bg-muted/40 lg:grid lg:grid-cols-[260px_1fr]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-[260px] bg-[#0F172A] text-white flex flex-col transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <div className="flex items-center justify-between px-6 h-20 border-b border-white/10">
          <span className="font-display font-black italic uppercase text-lg">Move<span className="text-[#2CACD4]">CMS</span></span>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X size={20} /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 no-scrollbar">
          {NAV_GROUPS.map((group) => (
            <div key={group.title} className="mb-5">
              <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40">{group.title}</p>
              {group.items.map((it) => {
                const Icon = ICONS[it.icon] || LayoutDashboard;
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${isActive ? "bg-[#2CACD4] text-slate-950" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
                    data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  >
                    <Icon size={17} />
                    <span className="flex-1">{it.label}</span>
                    {it.label === "Submissions" && newCount > 0 && (
                      <span className="rounded-full bg-[#DB1A5C] px-2 py-0.5 text-[10px] font-bold text-white">{newCount}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="mb-3">
            <p className="text-sm font-bold truncate">{user.name || user.email}</p>
            <p className="text-xs uppercase tracking-wider text-[#CAE295]">{user.role}</p>
          </div>
          <button onClick={logout} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors cursor-pointer" data-testid="admin-logout">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Content */}
      <div className="min-w-0">
        <header className="lg:hidden sticky top-0 z-20 flex items-center gap-3 bg-background border-b border-border px-4 h-16">
          <button onClick={() => setOpen(true)} aria-label="Open menu" data-testid="admin-menu-toggle"><Menu size={22} /></button>
          <span className="font-display font-black italic uppercase">MoveCMS</span>
        </header>
        <main className="p-4 sm:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
